// request payload schemas — shared by the worker (server-side validation) and the SPA
// (form validation), so the two never drift.
import { z } from 'zod';
import { TASK_TYPES } from '../domain/schedule.js';
import { LOADING_ITEMS } from '../domain/loading.js';
import { LOADING_PDI_MAX_ALIASES } from '../domain/loadingPdi.js';
import { PALETTE_IDS, UI_THEME_IDS } from '../domain/prefs.js';
// roles are data-driven (admin-managed), so a role reference is a validated string, not an enum.
const roleName = z.string().min(1).max(60).regex(/^[a-z0-9_]+$/, 'lowercase letters, numbers, underscore');
export const CellWriteSchema = z.object({
    columnId: z.number().int().positive(),
    value: z.unknown().optional(),
    format: z.string().max(200).optional(),
});
export const OverviewWriteSchema = z.object({
    season: z.string().optional(),
    sheetId: z.string().min(1),
    rowId: z.number().int().positive(),
    cells: z.array(CellWriteSchema).min(1).max(50),
});
// overview delivery-confirmation flags — stored app-side in D1 (NOT in the shared
// Smartsheet), keyed by SAP. two independent sign-offs: sales and logistics.
export const OverviewConfirmSchema = z.object({
    sap: z.string().min(1).max(50),
    field: z.enum(['sales', 'logistics']),
    value: z.boolean(),
});
// per-vessel Assembly order tracking — stored app-side in D1, keyed by SAP. a write patches any
// subset of the fields (printed date, leader-checked, comments); omitted fields are left as they are.
export const AssemblyOrderStatusSchema = z
    .object({
    sap: z.string().min(1).max(80),
    printedAt: z.string().regex(/^(\d{4}-\d{2}-\d{2})?$/, 'expected YYYY-MM-DD or empty').optional(),
    leaderChecked: z.boolean().optional(),
    comments: z.string().max(2000).optional(),
})
    // reject a no-op body (just a sap) so it can't create a phantom all-null row.
    .refine((v) => v.printedAt !== undefined || v.leaderChecked !== undefined || v.comments !== undefined, {
    message: 'at least one of printedAt, leaderChecked or comments is required',
});
// native order sheet (SHEET 27) — per-vessel spec cells stored app-side, keyed by SAP. A set patches
// one or more cells (column letter -> value); import seeds many vessels at once from the Excel.
// `sap` here is the row's storage key: a real SAP for overview vessels, or a synthetic key for a boat
// that lives only in the imported Excel ("name:<NAME>" when it has no SAP), so it can be a touch longer.
export const OrderSheetSetSchema = z.object({
    sap: z.string().min(1).max(80),
    cells: z.record(z.string().max(2000)),
});
export const OrderSheetImportSchema = z.object({
    rows: z
        .array(z.object({
        sap: z.string().min(1).max(80),
        data: z.record(z.string().max(2000)),
        name: z.string().max(200).optional(),
        model: z.string().max(120).optional(),
    }))
        .max(1000),
});
// remove a vessel from the order sheet (app-side hide + clears its stored cells; Smartsheet untouched)
// or restore a previously removed one.
export const OrderSheetDeleteSchema = z.object({ sap: z.string().min(1).max(80) });
// ── assembly weights template edits (production Smartsheet "Model" blocks) ──────────────────
// All three edit the per-model template only; the server rejects any row that isn't part of a Model
// block (so boat rows are never touched) and any sheet that isn't a current-season production sheet.
export const TemplateCellSchema = z
    .object({
    sheetId: z.string().min(1),
    rowId: z.number().int().positive(),
    name: z.string().trim().min(1).max(400).optional(),
    weight: z.number().min(0).max(100000).optional(),
})
    .refine((v) => v.name !== undefined || v.weight !== undefined, { message: 'name or weight is required' });
export const TemplateAddSchema = z.object({
    sheetId: z.string().min(1),
    parentRowId: z.number().int().positive(),
    kind: z.enum(['section', 'task']),
    name: z.string().min(1).max(400),
    weight: z.number().min(0).max(100000).optional(),
});
export const TemplateDeleteSchema = z.object({
    sheetId: z.string().min(1),
    rowId: z.number().int().positive(),
});
// reorder a template task: place `rowId` adjacent to `siblingId` (before it when above, else after).
export const TemplateMoveSchema = z.object({
    sheetId: z.string().min(1),
    rowId: z.number().int().positive(),
    siblingId: z.number().int().positive(),
    above: z.boolean().optional().default(false),
});
// ── overtime roster (the "Name Manager" employee list behind the printable overtime cards) ──────
const overtimeName = z.string().trim().min(1).max(120);
export const OvertimeAddSchema = z.object({ name: overtimeName });
export const OvertimeRemoveSchema = z.object({ name: overtimeName });
export const OvertimeImportSchema = z.object({ names: z.array(overtimeName).min(1).max(1000) });
export const PdiWriteSchema = z.object({
    season: z.string().optional(),
    sheetId: z.string().min(1),
    rowId: z.number().int().positive(),
    cells: z.array(CellWriteSchema).min(1).max(10),
});
// Loading checklist. An item edit is a partial patch: only the fields sent change, so a comment edit
// doesn't re-assert the status (which would also push it to PDI).
const loadingSap = z.string().trim().min(1).max(20);
export const LoadingStatusSchema = z.enum(['pending', 'loaded', 'na']);
export const LoadingItemPatchSchema = z
    .object({
    sap: loadingSap,
    itemNo: z.number().int().min(0).max(LOADING_ITEMS.length - 1),
    status: LoadingStatusSchema.optional(),
    comment: z.string().transform((s) => s.slice(0, 500)).optional(),
    date: z
        .string()
        .transform((s) => s.slice(0, 10))
        .refine((s) => s === '' || /^\d{4}-\d{2}-\d{2}$/.test(s), 'Use YYYY-MM-DD')
        .optional(),
})
    .refine((b) => b.status !== undefined || b.comment !== undefined || b.date !== undefined, 'Nothing to change');
export const LoadingBulkSchema = z.object({ sap: loadingSap, status: LoadingStatusSchema });
/** send a boat's Loading changes that haven't reached PDI yet. */
export const LoadingPushSchema = z.object({ sap: loadingSap, includeHolds: z.boolean().optional().default(false) });
/** Admin › Mappings: loading item name → extra PDI names that count as that item. */
export const LoadingPdiAliasesSchema = z.object({
    aliases: z.record(z.string().max(120), z.array(z.string().trim().min(1).max(120)).max(LOADING_PDI_MAX_ALIASES)),
});
// the client sends only sap + rowId + done; the worker resolves the sheet + status
// column server-side (from the vessel), so a caller can never target an arbitrary column.
export const TaskWriteSchema = z.object({
    season: z.string().optional(),
    /** the boat's SAP: blank while it hasn't been given one, so `boat` is what finds it */
    sap: z.string(),
    /** the boat's own row (its title row on the production sheet) */
    boat: z.number().int().positive().optional(),
    rowId: z.number().int().positive(),
    done: z.boolean(),
});
// rename a production row from Assembly Progress: the boat itself, a section heading or a task
export const ProductionRenameSchema = z.object({
    season: z.string().optional(),
    sap: z.string(),
    boat: z.number().int().positive().optional(),
    rowId: z.number().int().positive(),
    name: z.string().trim().min(1).max(200),
});
export const SetRoleSchema = z.object({
    email: z.string().email(),
    role: roleName,
});
export const SetActiveSchema = z.object({
    email: z.string().email(),
    active: z.boolean(),
});
// admin editing another user's display name.
export const SetUserNameSchema = z.object({
    email: z.string().email(),
    displayName: z.string().min(1).max(120),
});
// ── roles & permissions administration (data-driven RBAC) ──────────────────────
export const SetUserRolesSchema = z.object({
    email: z.string().email(),
    roles: z.array(roleName).max(30),
});
export const SetUserOverridesSchema = z.object({
    email: z.string().email(),
    overrides: z.array(z.object({ permission: z.string().min(1).max(60), granted: z.boolean() })).max(200),
});
export const CreateRoleSchema = z.object({
    name: roleName,
    label: z.string().min(1).max(60),
    description: z.string().max(400).optional().default(''),
});
export const UpdateRoleSchema = z.object({
    name: roleName,
    label: z.string().min(1).max(60).optional(),
    description: z.string().max(400).optional(),
});
export const RoleNameSchema = z.object({ name: roleName });
export const SetRolePermissionsSchema = z.object({
    role: roleName,
    permissions: z.array(z.string().min(1).max(60)).max(200),
});
// ── App-level auth (for workers.dev / no-Access deployments) ───────────────────
export const LoginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1).max(200),
});
export const CreateUserSchema = z.object({
    email: z.string().email(),
    displayName: z.string().max(120).optional().default(''),
    role: roleName,
    password: z.string().min(8).max(200),
});
// admin permanently deleting a user account (identity + access; history is preserved).
export const DeleteUserSchema = z.object({
    email: z.string().email(),
});
export const SetPasswordSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8).max(200),
});
// Self-service profile — a user editing their OWN display name + avatar. avatar is a small
// data: URL image (client resizes to ~128px); null clears it.
export const UpdateProfileSchema = z
    .object({
    displayName: z.string().min(1).max(120).optional(),
    avatar: z
        .string()
        .max(400_000)
        .regex(/^data:image\/(png|jpeg|webp);base64,/, 'must be an image data URL')
        .nullable()
        .optional(),
})
    .refine((o) => o.displayName !== undefined || o.avatar !== undefined, { message: 'nothing to update' });
// Self-service UI preferences — theme (light/dark/auto-by-hour), region locale, date format, and
// the one-time first-run "chose a theme" flag. All optional; a save merges into the stored blob.
export const PreferencesSchema = z.object({
    theme: z.enum(['light', 'dark', 'auto']).optional(),
    region: z.string().max(20).optional(),
    dateFormat: z.string().max(20).optional(),
    themeChosen: z.boolean().optional(),
    cursorHalo: z.boolean().optional(), // show the mouse-follow highlight halo (default on)
    lastSeenAnnId: z.number().int().nonnegative().optional(), // highest announcement id acknowledged
    logo: z.enum(['silver', 'blue', 'red', 'green', 'gold', 'stock', 'burgundy']).optional(), // logo colourway (stock/burgundy = legacy)
    hideHeaders: z.boolean().optional(), // hide the big page titles for a compact view
    cellHistory: z.boolean().optional(), // the change-history card on hovering a cell (default on)
    hoverTips: z.boolean().optional(), // the explanation cards on hovering (default on)
    palette: z.enum(PALETTE_IDS).optional(), // colour profile
    paletteChosen: z.boolean().optional(), // has the one-time colour-theme choice been made?
    uiTheme: z.enum(UI_THEME_IDS).optional(), // whole-app theme (Evolved, High contrast, Stegi)
});
// ── DS / programming ──────────────────────────────────────────────────────────
export const DsWriteSchema = z
    .object({
    rowId: z.number().int().positive(),
    ds: z.boolean().optional(),
    programmed: z.boolean().optional(),
    comment: z.string().max(2000).optional(),
})
    .refine((o) => o.ds !== undefined || o.programmed !== undefined || o.comment !== undefined, {
    message: 'nothing to write',
});
// ── schedule (collaboration) ──────────────────────────────────────────────────
export const IdSchema = z.object({ id: z.string().min(1).max(64) });
export const ScheduleVesselSchema = z.object({
    id: z.string().max(64).optional(),
    sap: z.string().max(50).optional().default(''),
    name: z.string().min(1).max(160),
    deliveryDate: z.string().max(20).optional().default(''),
    notes: z.string().max(1000).optional().default(''),
    hidden: z.boolean().optional().default(false),
});
// bulk import of vessels from the Smartsheet overview into the schedule. the client sends
// only the selected SAPs + a flag; the worker fetches the overview server-side and upserts
// (keyed by SAP so re-import never duplicates and refreshes delivery date + confirmation).
export const ScheduleImportSchema = z.object({
    season: z.string().max(20).optional(),
    saps: z.array(z.string().min(1).max(50)).min(1).max(500),
    autoTasks: z.boolean().optional().default(true),
});
const YMD = /^\d{4}-\d{2}-\d{2}$/;
export const ScheduleTaskSchema = z
    .object({
    id: z.string().max(64).optional(),
    vesselId: z.string().min(1).max(64),
    type: z.enum(TASK_TYPES),
    start: z.string().regex(YMD),
    end: z.string().regex(YMD),
    notes: z.string().max(500).optional().default(''),
    completed: z.boolean().optional().default(false),
})
    .refine((t) => t.end >= t.start, { message: 'end must be on or after start', path: ['end'] });
// ── agenda + pins ─────────────────────────────────────────────────────────────
export const AgendaEntrySchema = z.object({
    id: z.string().max(64).optional(),
    date: z.string().regex(YMD),
    time: z.string().max(8).optional().default(''),
    endTime: z.string().max(8).optional().default(''),
    endDate: z.string().max(20).optional().default(''),
    title: z.string().min(1).max(200),
    text: z.string().max(5000).optional().default(''),
    color: z.string().max(20).optional().default(''),
    done: z.boolean().optional().default(false),
    sharedWith: z.array(z.string().email()).max(50).optional().default([]),
});
export const AgendaNoteSchema = z.object({
    date: z.string().regex(YMD),
    text: z.string().max(10000).optional().default(''),
});
export const PinSchema = z.object({
    text: z.string().min(1).max(1000),
    color: z.string().max(20).optional().default(''),
});
// ── sales orders (Configurator) ───────────────────────────────────────────────
export const SalesOrderSchema = z
    .object({
    id: z.string().max(64).optional(),
    sap: z.string().max(50).optional().default(''),
    model: z.string().max(80).optional().default(''),
    vesselName: z.string().max(160).optional().default(''),
    buyer: z.string().max(200).optional().default(''),
    payload: z
        .record(z.unknown())
        .optional()
        .default({})
        .refine((p) => JSON.stringify(p).length <= 200_000, { message: 'payload too large' }),
})
    .refine((o) => !!(o.sap.trim() || o.vesselName.trim()), {
    message: 'a SAP or Vessel Name is required',
    path: ['vesselName'],
});
//# sourceMappingURL=api.js.map